import SEO from '@/components/SEO';
import { site } from '@/data/content';
import {
  BookOpen,
  Search,
  BarChart3,
  MessageSquare,
  Ticket,
  ArrowUpRight,
  Check,
  Users,
  Globe,
  Building2,
  Sparkles,
  Wand2,
  Palette,
  Code2,
} from 'lucide-react';

const features = [
  {
    icon: BookOpen,
    title: 'Public help center',
    text: 'Publish a branded, hosted help center customers can reach from any device — no code or separate host needed.',
  },
  {
    icon: Search,
    title: 'Instant search',
    text: 'Customers find answers fast with full-text search across every article, category, and department.',
  },
  {
    icon: Wand2,
    title: 'AI article drafts',
    text: 'Zyra AI drafts articles from a prompt, bullet points, or past replies. Your team edits and publishes in minutes.',
  },
  {
    icon: Palette,
    title: 'Free & premium themes',
    text: 'Switch between clean presets or unlock premium themes like Ocean, Midnight, Berry, and Royal for $190 one-time.',
  },
  {
    icon: Code2,
    title: 'Custom CSS',
    text: 'Bring your own CSS for full control over fonts, spacing, colors, and layout — perfect for a pixel-perfect brand match.',
  },
  {
    icon: Sparkles,
    title: 'AI-assisted answers',
    text: 'Zyra surfaces the right articles to agents and suggests relevant help content inside every conversation.',
  },
  {
    icon: BarChart3,
    title: 'Search analytics',
    text: 'See what customers are searching for and which articles are missing, so your content keeps improving.',
  },
];

const useCases = [
  {
    icon: Globe,
    title: 'E-commerce & retail',
    text: 'Publish shipping, returns, and sizing guides to cut WISMO tickets and boost repeat purchases.',
  },
  {
    icon: Building2,
    title: 'B2B services',
    text: 'Host onboarding docs, implementation guides, and SLAs so enterprise customers self-serve 24/7.',
  },
  {
    icon: Users,
    title: 'Customer onboarding',
    text: 'Turn FAQs into searchable articles so new users find answers without waiting on support.',
  },
];

const comparison = [
  { feature: 'Public help center', zurihub: true, zendesk: true, intercom: true, freshdesk: true },
  { feature: 'Custom domain included', zurihub: true, zendesk: 'add-on', intercom: 'add-on', freshdesk: 'add-on' },
  { feature: 'AI article drafts', zurihub: true, zendesk: 'add-on', intercom: 'add-on', freshdesk: false },
  { feature: 'Free & premium themes', zurihub: true, zendesk: 'limited', intercom: 'limited', freshdesk: 'limited' },
  { feature: 'Custom CSS', zurihub: true, zendesk: false, intercom: false, freshdesk: false },
  { feature: 'Premium theme price', zurihub: '$190 one-time', zendesk: 'N/A', intercom: 'N/A', freshdesk: 'N/A' },
  { feature: 'Ticket deflection from articles', zurihub: true, zendesk: 'partial', intercom: false, freshdesk: 'partial' },
  { feature: 'AI article suggestions', zurihub: true, zendesk: true, intercom: true, freshdesk: false },
  { feature: 'Knowledge base on free plan', zurihub: true, zendesk: false, intercom: false, freshdesk: false },
  { feature: 'Starting price per agent', zurihub: '$25', zendesk: '$99', intercom: '$99', freshdesk: '$59' },
];

export default function Knowledgebase() {
  return (
    <>
      <SEO
        title="Knowledge Base"
        description="ZuriHub's knowledge base turns repeat questions into self-service with a public, branded help center, AI article drafts, free and premium themes, custom CSS, and built-in ticket deflection."
        path="/knowledgebase"
      />

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-20 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-4xl text-center">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Knowledge <span className="gradient-text">Base</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
            A public, branded help center with AI drafts, premium themes, and custom CSS — so your customers self-serve in style.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
          >
            Start building your help center <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Knowledgebase features</h2>
            <p className="section-subtitle mx-auto mt-4">
              Everything you need to publish, manage, and improve a modern help center.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="uses-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="uses-heading" className="text-center section-title">How businesses use our knowledge base</h2>
          <p className="section-subtitle mx-auto mt-4 text-center">
            Teams of every size use the ZuriHub knowledge base to scale support without scaling headcount.
          </p>
          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-brand-100 text-brand-600">
                  <u.icon className="h-5 w-5" />
                </div>
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="tickets-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="tickets-heading" className="section-title">Self-serve portal for tickets</h2>
              <p className="mt-4 text-lg text-slate-600">
                Not every answer belongs in an article. When customers need personal help, they can submit a
                ticket directly from the knowledge base — no context switching, no lost history.
              </p>
              <ol className="mt-8 space-y-4">
                <li className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">1</span>
                  <span className="text-slate-700">Customer searches the help center for an answer.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">2</span>
                  <span className="text-slate-700">If the article does not solve it, they click Submit a Ticket from the header or footer.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">3</span>
                  <span className="text-slate-700">The ticket is created in the same workspace agents already use, with the article attached.</span>
                </li>
              </ol>
              <a
                href={`${site.workspaceUrl}/kb?workspaceSlug=demo&ticket=open`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-600 px-7 py-3.5 text-base font-bold text-white transition hover:bg-brand-700"
              >
                <Ticket className="h-4 w-4" /> See the ticket flow
              </a>
            </div>
            <div className="rounded-3xl bg-slate-900 p-8 text-white shadow-xl">
              <div className="flex items-center justify-between border-b border-slate-700 pb-4">
                <div className="flex items-center gap-3">
                  <BookOpen className="h-5 w-5 text-brand-400" />
                  <span className="font-bold">Help Center</span>
                </div>
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-slate-400" />
                  <MessageSquare className="h-4 w-4 text-slate-400" />
                </div>
              </div>
              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-3 rounded-2xl bg-slate-800 p-4">
                  <Search className="h-5 w-5 text-slate-400" />
                  <span className="text-sm text-slate-300">How do I reset my password?</span>
                </div>
                <div className="rounded-2xl border-l-4 border-brand-500 bg-slate-800 p-4">
                  <h4 className="font-semibold">Reset your password</h4>
                  <p className="mt-1 text-sm text-slate-300">
                    Go to Settings → Security → Change Password and follow the confirmation steps.
                  </p>
                </div>
                <div className="rounded-2xl bg-brand-600 p-4 text-center text-sm font-semibold">
                  Still stuck? Submit a Ticket
                </div>
                <div className="rounded-2xl border border-slate-700 bg-slate-800/50 p-4 text-sm text-slate-300">
                  <Ticket className="mr-2 inline h-4 w-4 text-brand-400" />
                  Ticket #4821 created. Track it in your workspace.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="compare-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="compare-heading" className="mb-10 text-center section-title">How we compare</h2>
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 text-left font-bold text-slate-700">Feature</th>
                    <th className="px-6 py-4 text-center font-bold text-brand-700">ZuriHub</th>
                    <th className="px-6 py-4 text-center font-bold text-slate-700">Zendesk</th>
                    <th className="px-6 py-4 text-center font-bold text-slate-700">Intercom</th>
                    <th className="px-6 py-4 text-center font-bold text-slate-700">Freshdesk</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row) => (
                    <tr key={row.feature} className="border-t border-slate-100">
                      <td className="px-6 py-4 font-medium text-slate-900">{row.feature}</td>
                      <td className="px-6 py-4 text-center">
                        {row.zurihub === true ? <Check className="mx-auto h-5 w-5 text-green-600" /> : <span className="font-semibold text-brand-700">{row.zurihub}</span>}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {row.zendesk === true ? <Check className="mx-auto h-5 w-5 text-green-600" /> : <span className="text-xs font-bold text-slate-500 uppercase">{row.zendesk}</span>}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {row.intercom === true ? <Check className="mx-auto h-5 w-5 text-green-600" /> : <span className="text-xs font-bold text-slate-500 uppercase">{row.intercom}</span>}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {row.freshdesk === true ? <Check className="mx-auto h-5 w-5 text-green-600" /> : <span className="text-xs font-bold text-slate-500 uppercase">{row.freshdesk}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Publish your help center today</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Start free and give your customers the self-service experience they expect.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Create your knowledge base <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
